import { TTranslation } from "@/features/FormCard/hooks";
import { CategoryOption, DEFAULT_MOVEMENT_MAP } from "./config";

/**
 * Configuration type for section filtering
 */
export type FilterConfig = {
  sectionName: string;
  allowedFields?: string[];
};

/**
 * Get filtered category options for C8 based on asset classes
 */
export function getAllowedMovements(assetClass2?: string | null): string[] {
  if (!assetClass2) return [];
  const movementMap = DEFAULT_MOVEMENT_MAP;
  const entry = movementMap[assetClass2];
  if (!entry) return [];
  if (Array.isArray(entry)) {
    return entry.slice();
  }
  return [];
}

/**
 * Get filtered category options based on account type and asset classes
 */
export function getFilteredCategoryOptions(
  options: CategoryOption[],
  assetClass2: string | null,
): CategoryOption[] {
  if (!options?.length || !assetClass2) return [];

  const allowed = getAllowedMovements(assetClass2);

  // Nothing allowed → nothing to show
  if (!allowed.length) return [];

  const allow = new Set(allowed);

  // Keep sections but only with allowed fields; drop empty sections
  return options
    .map((sections) => {
      const filteredFields = (sections.fields ?? []).filter((f) =>
        allow.has(f),
      );
      return { ...sections, fields: filteredFields };
    })
    .filter((sections) => sections.fields.length > 0);
}

/**
 * Flattens options from sectioned format to a list of option values
 */
export function extractOptionsValues(options: any[]): string[] {
  if (!options || !Array.isArray(options) || options.length === 0) {
    return [];
  }

  // Handle sectioned options (with items property)
  if ("items" in options[0]) {
    return options.flatMap((section) =>
      section.items ? section.items.map((item: any) => item.label) : [],
    );
  }

  // Handle flat options (with value property)
  if ("value" in options[0]) {
    return options.map((option) => option.label);
  }

  return [];
}

/**
 * Check if should auto-select the category option
 *
 * @param options List of available options
 * @param currentValue Current field value
 * @param isCategoryDisabled Whether the category field is disabled
 * @param translations Optional translations for category values
 * @returns Object with shouldSelect flag and selectedValue if applicable
 */
export function shouldAutoSelectOption(
  options: any[],
  currentValue: string,
  isCategoryDisabled: boolean,
  keysAllowNullValue: string[] = [],
): { shouldSelect: boolean; selectedValue?: string } {
  // Don't auto-select if options are invalid or field is disabled
  if (!options || !Array.isArray(options) || isCategoryDisabled) {
    return { shouldSelect: false };
  }

  const availableOptions = extractOptionsValues(options);

  if (availableOptions.length === 1) {
    const key = availableOptions[0];

    if (currentValue === key || keysAllowNullValue.includes(key)) {
      return { shouldSelect: false };
    }

    return { shouldSelect: true, selectedValue: key };
  }

  return { shouldSelect: false };
}

/**
 * Check if asset class has changed between previous and current value
 *
 * @param prevAssetClass Previous asset class value
 * @param currentAssetClass Current asset class value
 * @returns Whether the asset class has changed and should trigger a reset
 */
export function shouldResetCategoryValue(
  prevAssetClass: string | undefined,
  currentAssetClass: string | undefined,
): boolean {
  return prevAssetClass !== undefined && prevAssetClass !== currentAssetClass;
}

/**
 * Get the key of a translation by its value
 *
 * @param translations Translations object
 * @param value Value to search for
 * @returns Key of the translation if found, undefined otherwise
 */
export function getKeyByTranslation(
  value: string,
  translations?: TTranslation["category"],
): string | undefined {
  return Object.keys(translations ?? {}).find(
    (key) => translations?.[key] === value,
  );
}
